/**
 * 积分流水列表（学生端与家长端共用）。
 * 只读展示；积分断言"孩子完全可见"（见 shared/permissions.ts），所以不做任何遮挡。
 */
import { POINTS_REASONS, type PointsReason } from '@fht/shared';
import type { PointsLedgerEntry } from '../lib/api/types';
import {
  LEDGER_AMOUNT_TONE_CLASS,
  ledgerAmountText,
  ledgerAmountTone,
} from '../lib/pointsDisplay';
import { EmptyState, Skeleton } from './ui/Card';
import { formatDateTimeFull } from '../lib/utils';

const REASON_LABEL: Record<PointsReason, string> = {
  checkin: '完成打卡',
  makeup: '补卡',
  bonus: '额外奖励',
  penalty: '扣分',
  redeem: '兑换奖品',
  redeem_refund: '兑换退回',
  adjust: '手动调整',
};

export function reasonLabel(reason: string): string {
  return REASON_LABEL[reason as PointsReason] ?? reason;
}

export function reasonIcon(reason: string): string {
  switch (reason) {
    case 'checkin':
      return '✅';
    case 'makeup':
      return '🕘';
    case 'bonus':
      return '🌟';
    case 'penalty':
      return '⚠️';
    case 'redeem':
      return '🎁';
    case 'redeem_refund':
      return '↩️';
    default:
      return '✏️';
  }
}

export interface PointsLedgerListProps {
  entries: PointsLedgerEntry[] | undefined;
  isLoading?: boolean;
  /** 儿童模式：更大字号与间距 */
  kid?: boolean;
  emptyHint?: string;
}

export function PointsLedgerList({
  entries,
  isLoading = false,
  kid = false,
  emptyHint = '完成打卡后，这里会记录每一笔积分变动。',
}: PointsLedgerListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return <EmptyState icon="🧾" title="暂无积分记录" description={emptyHint} />;
  }

  return (
    <ul className="divide-y divide-slate-100">
      {entries.map((entry) => {
        const tone = ledgerAmountTone(entry.delta);
        return (
          <li key={entry.id} className="flex items-center gap-3 py-3">
            <span aria-hidden="true" className={kid ? 'text-2xl' : 'text-xl'}>
              {reasonIcon(entry.reason)}
            </span>
            <div className="min-w-0 flex-1">
              <p className={kid ? 'text-lg font-medium text-slate-900' : 'font-medium text-slate-900'}>
                {reasonLabel(entry.reason)}
              </p>
              <p className="truncate text-xs text-slate-500">
                {entry.note || '—'} · {formatDateTimeFull(entry.createdAt)}
              </p>
            </div>
            <span className={`${kid ? 'text-xl font-bold' : 'font-semibold'} ${LEDGER_AMOUNT_TONE_CLASS[tone]}`}>
              {ledgerAmountText(entry.delta)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** 积分原因的全集：便于将来做筛选（目前只在"关于积分"说明里列出）。 */
export const ALL_POINTS_REASONS = POINTS_REASONS;
